import React, { useState } from "react"
import { IconMail, IconMapPin, IconUsers } from "@tabler/icons-react"
import type { IChapterCellGroup } from "@dc-ireland/shared"
import { buildInterestMailto, chapterSectionContainerClass, chapterSectionContentClass } from "../../../lib/chapters"
import LandingCarousel from "../LandingCarousel"
import LandingEmptyState from "../LandingEmptyState"
import LandingReveal from "../LandingReveal"
import LandingSectionHeader from "../LandingSectionHeader"
import LandingCellGroupJoinModal from "../LandingCellGroupJoinModal"

type Props = {
  chapterName: string
  cellGroups: IChapterCellGroup[]
  loading?: boolean
}

const cardWrapperClass = "shrink-0 snap-start basis-[220px] sm:basis-[240px]"

const mailtoButtonClass =
  "inline-flex items-center justify-center gap-1.5 rounded-full bg-[#9e7e43] px-5 py-2.5 font-dm-sans text-sm font-semibold text-white transition hover:bg-[#b8954f]"

const ChapterCellGroupsSection: React.FC<Props> = ({ chapterName, cellGroups, loading = false }) => {
  const generalMailto = buildInterestMailto(chapterName)
  const [selectedGroup, setSelectedGroup] = useState<IChapterCellGroup | null>(null)

  return (
    <>
      <section id="cell-groups" className="scroll-mt-28 bg-white py-16 md:py-24">
        <div className={chapterSectionContainerClass}>
          <LandingSectionHeader
            eyebrow="Connect Locally"
            title="Cell Groups"
            description="Find a small group near you for fellowship, prayer, and growth in the Word."
          />

          <div className={chapterSectionContentClass}>
            {loading ? (
              <p className="text-center font-dm-sans text-sm text-[#5a5a5a]">Loading cell groups…</p>
            ) : cellGroups.length === 0 ? (
              <LandingEmptyState
                icon={IconUsers}
                pattern="sparkle"
                title="No cell groups listed yet"
                description="Cell groups for this chapter will appear here once they're added."
                action={
                  <a href={generalMailto} className={mailtoButtonClass}>
                    <IconMail size={16} />
                    Get in Touch
                  </a>
                }
              />
            ) : (
              <LandingReveal delay={0.08}>
                <LandingCarousel ariaLabel={`${chapterName} cell groups carousel`}>
                  {cellGroups.map((group) => (
                    <div key={group.id ?? group.name} className={cardWrapperClass}>
                      <article className="flex h-full flex-col rounded-2xl border border-[#ebebeb] bg-[#fafafa] p-4 transition duration-300 hover:border-[#9e7e43]/30 hover:shadow-[0_12px_40px_-16px_rgba(158,126,67,0.15)] sm:p-5">
                        <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-[#f4f7ff] text-dc-primary-900">
                          <IconMapPin size={18} stroke={1.5} />
                        </div>
                        <h3 className="font-outfit text-base font-semibold text-[#1f1f1f]">{group.name}</h3>
                        {group.area ? (
                          <p className="mt-1 font-dm-sans text-sm text-[#5a5a5a]">{group.area}</p>
                        ) : null}
                        {group.meetingDay ? (
                          <p className="mt-2 font-dm-sans text-[11px] font-semibold uppercase tracking-wider text-[#9e7e43]">
                            {group.meetingDay}
                          </p>
                        ) : null}
                        <div className="mt-auto pt-4">
                          {group.id ? (
                            <button
                              type="button"
                              onClick={() => setSelectedGroup(group)}
                              className={`${mailtoButtonClass} w-full`}
                            >
                              <IconMail size={16} />
                              Get in Touch
                            </button>
                          ) : (
                            <a href={generalMailto} className={`${mailtoButtonClass} w-full`}>
                              <IconMail size={16} />
                              Get in Touch
                            </a>
                          )}
                        </div>
                      </article>
                    </div>
                  ))}
                </LandingCarousel>
              </LandingReveal>
            )}
          </div>
        </div>
      </section>

      <LandingCellGroupJoinModal
        open={Boolean(selectedGroup)}
        cellGroupId={selectedGroup?.id}
        cellGroupName={selectedGroup?.name}
        onClose={() => setSelectedGroup(null)}
      />
    </>
  )
}

export default ChapterCellGroupsSection
